import React, {useState} from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import {ApprovalForm} from "./ApprovalForm";
import {NoActionForm} from "./NoActionForm";
import {UploadForm} from "./UploadForm";
import {Loader} from "../../common/Loader";
import {Stage, UploadStageFile} from "../../../api/stage/getStages";

const initialBaseFormData = {
    description: '',
    id: '',
    title: '',
}

export const initialNoActionFormData: Stage = {
    ...initialBaseFormData,
    files: undefined,
    rejectionMessage: undefined,
    type: 'NO_ACTION',
    waitTime: 0
}

export const initialApprovalFormData: Stage = {
    ...initialBaseFormData,
    files: undefined,
    rejectionMessage: '',
    type: 'APPROVAL',
    waitTime: 0
}

export const initialFile: UploadStageFile = {title: '', type: 'image'}

export const initialUploadFormData: Stage = {
    ...initialBaseFormData,
    files: [initialFile],
    rejectionMessage: undefined,
    type: 'UPLOAD',
    waitTime: undefined
}

type AddEditModalProps = {
    onClose: (shouldRefetch: boolean) => void,
    stage: undefined | Stage,
}

export const AddEditModal = (props: AddEditModalProps) => {
    const {onClose, stage} = props

    const [isLoading, setIsLoading] = useState(false)
    const [formData, setFormData] = useState<Stage>(stage === undefined ? initialNoActionFormData : stage)

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const updateFormData = (newValues: Partial<typeof initialBaseFormData>) => {
        setFormData(oldState => ({
            ...oldState,
            ...newValues,
        }))
    }

    return (
        <Dialog
            fullScreen={fullScreen}
            open={true}
            onClose={() => onClose(false)}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle>
                {stage === undefined ? 'Add New Stage' : `Edit ${stage.title}`}
            </DialogTitle>
            <DialogContent>
                <Stack gap="1.5rem">
                    <FormControl>
                        <FormLabel id="stage-type-label">Stage Type</FormLabel>
                        <RadioGroup
                            row
                            aria-labelledby="stage-type-label"
                            value={formData.type}
                            onChange={(e) => {
                                switch (e.target.value) {
                                    case 'NO_ACTION': updateFormData({...initialNoActionFormData, id: formData.id}); break;
                                    case 'UPLOAD': updateFormData({...initialUploadFormData, id: formData.id}); break;
                                    case 'APPROVAL': updateFormData({...initialApprovalFormData, id: formData.id}); break;
                                }
                            }}
                        >
                            <FormControlLabel value="NO_ACTION" control={<Radio />} label="No Action" />
                            <FormControlLabel value="UPLOAD" control={<Radio />} label="Upload" />
                            <FormControlLabel value="APPROVAL" control={<Radio />} label="Approval" />
                        </RadioGroup>
                    </FormControl>
                </Stack>
            </DialogContent>

            {formData.type === 'NO_ACTION' && (
                <NoActionForm
                    formData={formData}
                    onClose={onClose}
                    setLoading={setIsLoading}
                    updateFormData={updateFormData}
                />
            )}

            {formData.type === 'UPLOAD' && (
                <UploadForm
                    formData={formData}
                    onClose={onClose}
                    setLoading={setIsLoading}
                    updateFormData={updateFormData}
                />
            )}

            {formData.type === 'APPROVAL' && (
                <ApprovalForm
                    formData={formData}
                    onClose={onClose}
                    setLoading={setIsLoading}
                    updateFormData={updateFormData}
                />
            )}

            <Loader isVisible={isLoading} />
        </Dialog>
    )
}
